"use client";

import { useState } from "react";
import {
  BookOpen,
  Users,
  Target,
  Briefcase,
  FileText,
  CheckCircle,
  Lightbulb,
  Layers,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@ui/dialog";
import { Programme } from "@/data/programmes";

// Define the structure of the 'prog' prop based on your data
interface ProgrammeInfoGridProps {
  prog: Programme | undefined;
}

const infoCards = [
  {
    key: "admission",
    title: "Admission Requirements",
    icon: FileText,
    color: "bg-healing-50 text-healing-800 border-healing-200",
  },
  {
    key: "curriculum",
    title: "Curriculum & Courses",
    icon: BookOpen,
    color: "bg-wisdom-50 text-wisdom-800 border-wisdom-200",
  },
  {
    key: "instruction",
    title: "Methods of Instruction",
    icon: Layers,
    color: "bg-nature-50 text-nature-800 border-nature-200",
  },
  {
    key: "outcomes",
    title: "Learning Outcomes",
    icon: Target,
    color: "bg-healing-50 text-healing-800 border-healing-200",
  },
  {
    key: "audience",
    title: "Who Is It For?",
    icon: Users,
    color: "bg-wisdom-50 text-wisdom-800 border-wisdom-200",
  },
  {
    key: "tuition",
    title: "Tuition & Fees",
    icon: Briefcase,
    color: "bg-nature-50 text-nature-800 border-nature-200",
  },
];

export function ProgrammeInfoGrid({ prog }: ProgrammeInfoGridProps) {
  const [activeCard, setActiveCard] = useState<string | null>(null);

  const renderCardContent = (key: string) => {
    switch (key) {
      case "admission":
        return (
          <div className="space-y-4">
            <ul className="space-y-3">
              {prog!.admissionRequirements.criteria.map(
                (c: string, i: number) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-healing-500 shrink-0 mt-0.5" />
                    <span className="text-foreground">{c}</span>
                  </li>
                ),
              )}
            </ul>
            <div className="mt-4 p-4 bg-muted rounded-xl">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Review Timeline:</strong>{" "}
                {prog!.admissionRequirements.reviewTimeline}
              </p>
            </div>
          </div>
        );
      case "curriculum":
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              {prog!.curriculum.composition}
            </p>
            <div className="grid gap-2">
              {prog!.curriculum.courses.map((c: string, i: number) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                >
                  <span className="w-7 h-7 rounded-full bg-wisdom-100 text-wisdom-700 flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-sm text-foreground">{c}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case "instruction":
        return (
          <div className="flex flex-wrap gap-3">
            {prog!.curriculum.methodsOfInstruction.map(
              (m: string, i: number) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-nature-50 text-nature-700 rounded-full text-sm font-medium border border-nature-200"
                >
                  {m}
                </span>
              ),
            )}
          </div>
        );
      case "outcomes":
        return (
          <div className="space-y-3">
            {prog!.learningOutcomes.map((o: string, i: number) => (
              <div key={i} className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-wisdom-500 shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{o}</span>
              </div>
            ))}
          </div>
        );
      case "audience":
        return (
          <div className="grid sm:grid-cols-2 gap-3">
            {prog!.overview.whoIsItFor.map((w: string, i: number) => (
              <div
                key={i}
                className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl"
              >
                <Users className="w-5 h-5 text-healing-500 shrink-0" />
                <span className="text-sm text-foreground">{w}</span>
              </div>
            ))}
          </div>
        );
      case "tuition":
        return (
          <div className="p-6 bg-muted/50 rounded-xl text-center">
            <Briefcase className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div
              className="text-foreground leading-6"
              dangerouslySetInnerHTML={{
                __html: prog!.overview.tuitionFee.information,
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-10">
            <h2 className="font-serif-display text-2xl md:text-3xl font-bold text-foreground mb-2">
              Programme Details
            </h2>
            <p className="text-muted-foreground">
              Click any card to explore more
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {infoCards.map((card, i) => {
              const IconComp = card.icon;
              return (
                <div
                  key={card.key}
                  className="animate-[fadeUp_0.5s_ease-out_both]"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <button
                    onClick={() => setActiveCard(card.key)}
                    className={`w-full text-left rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:scale-[1.02] ${card.color}`}
                  >
                    <IconComp className="w-8 h-8 mb-3" />
                    <h3 className="font-semibold text-lg">{card.title}</h3>
                    <p className="text-sm mt-1">Click to expand</p>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Dialog open={!!activeCard} onOpenChange={() => setActiveCard(null)}>
        <DialogContent className="max-w-[90vw] max-h-[70vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl">
              {infoCards.find((c) => c.key === activeCard)?.title}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Details about {infoCards.find((c) => c.key === activeCard)?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {activeCard && renderCardContent(activeCard)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
